import { GetSize } from '@assets/component/GetSize/GetSize';
import {
    PanelColumn,
    PanelRow,
    PanelTable,
    TableField
} from '@assets/component/Panel/PanelTable/PanelTable';
import { PanelTitle } from '@assets/component/Panel/PanelTitle/PanelTitle';
import { useStore, useStoreUI } from '@assets/model/Store';
import { throttle } from 'lodash';
import { observer } from 'mobx-react';
import { useCallback } from 'react';
import {
    formatDollar,
    formatPercent,
    formatPercentNoMultiply
} from '../PanelElements/Formatters';
import { RowLabel } from '../PanelElements/RowLable/RowLable';
import { RowValue } from '../PanelElements/RowValue/RowValue';
import { onEditReportField } from '../UpdateFieldsUtil';
import {
    EBITDAFieldsKeys,
    FieldsSchema,
    SchemaFieldKeys
} from '@assets/model/FieldsSchema';
import _ from 'lodash';
import { DataAccessUtils } from '@assets/model/DataAccessUtils';
import * as style from './PanelFinanceYearly.module.less';
import { Schema } from 'zod';
import { getFloat } from '@assets/utils/Formatting';

export const PanelFinanceYearly = observer(() => {
    const store = useStore();
    const yearsShow = useStoreUI().detailsViewYearsToShow;
    const minPixelsPerEntry = 110;
    //const maxYearsInReport = 8;

    const setYears = useCallback(
        throttle(
            year => {
                useStoreUI().detailsViewYearsToShow = year;
            },
            800,
            { leading: false, trailing: true }
        ),
        []
    );

    const onResize = (size: { width: number; height: number }) => {
        if (size.width == 0) {
            return;
        }

        const calcYears = Math.ceil((size.width - 300) / minPixelsPerEntry);

        if (yearsShow !== calcYears) {
            setYears(calcYears);
        }
    };

    const maxYearsInReport = yearsShow;
    //console.log(maxYearsInReport);
    const query = store.query;
    const reportData = query.activeReport;

    // const findByYearSection = (
    //     req: SectionValueByYearRequest,
    //     yearOffset?: number
    // ) => {
    //     return (field: TableField, column: TableField) => {
    //         const year = parseInt(column.source) + (yearOffset ?? 0);

    //         return query.valueBySection({
    //             section: req.section,
    //             field: req.field,
    //             year
    //         });
    //     };
    // };

    const findByYear = (yearOffset: number = 0) => {
        return (field: TableField, column: TableField) => {
            const year = parseInt(column.source) + yearOffset;
            return query.valueByYear(field.source as SchemaFieldKeys, year);
        };
    };

    const findByYearDivideByRevenue = (yearOffset: number = 0) => {
        return (field: TableField, column: TableField) => {
            const year = parseInt(column.source) + yearOffset;
            const fieldValue = query.valueByYear(
                field.source as SchemaFieldKeys,
                year
            );
            const revenue = query.valueByYear('net_revenue_per_year', year);
            if (fieldValue === 'N/A' || revenue === 'N/A') {
                return 'N/A';
            }

            return fieldValue / revenue;
        };
    };

    const createColumnsYears = (
        fromYear: number,
        toYear: number
    ): PanelColumn[] => {
        const actualizedYears =
            reportData['list_years_actualized_revenue'] ?? [];

        const minActualizedYear = Math.min(...actualizedYears);

        const cols: PanelColumn[] = [];
        for (let year = fromYear; year <= toYear; year++) {
            const isActualized =
                actualizedYears.includes(year) || year <= minActualizedYear;
            const letter = isActualized ? 'A' : 'P';

            cols.push({
                column: {
                    source: year.toString(),
                    display: year.toString() + letter
                }
            });
        }

        return cols;
    };

    const getReportYearRange = () => {
        if (!reportData) {
            return { from: 0, to: 0 };
        }

        var minYear = -1;
        var maxYear = -1;
        const fields = ['net_revenue_per_year', 'ebitda_per_year'];

        fields.forEach(field => {
            const list = reportData[field];
            if (!list || Array.isArray(list) === false) {
                return;
            }
            const years = list.map((item: any) => parseInt(item.year));
            const min = Math.min(...years);
            const max = Math.max(...years);
            if (minYear == -1 || min < minYear) {
                minYear = min;
            }
            if (maxYear == -1 || max > maxYear) {
                maxYear = max;
            }
        });

        //minYear = 2003;
        if (maxYear - minYear > maxYearsInReport) {
            const trim = maxYear - minYear - maxYearsInReport;
            return { from: minYear + trim, to: maxYear };
        }

        //console.log(minYear, maxYear);
        return { from: minYear, to: maxYear };
    };

    const tableRange = getReportYearRange();

    const columns: PanelColumn[] = [
        ...createColumnsYears(tableRange.from, tableRange.to),
        {
            column: { source: null, display: 'Source' },
            element: RowValue.sources,
            dontUseRowFormatting: true,
            overrideValue: query.sources.bind(query)
        }
    ];

    const defaults = {
        label: RowLabel.label,
        formatter: formatDollar,
        element: RowValue.dollar,
        editable: true
    };

    const defaultsPercent = {
        label: RowLabel.percent_label,
        formatter: formatPercent,
        element: RowValue.percent
    };

    const makeRow = (
        fieldName: SchemaFieldKeys,
        hideRowNoData: boolean = false
    ): PanelRow => {
        const hasData = query.anyValue(fieldName);
        const display = FieldsSchema[fieldName]['display'] ?? fieldName;

        const row = {
            ...defaults,
            skipRow: !hasData && hideRowNoData,
            field: {
                source: fieldName,
                display: display
            },
            value: findByYear()
        };

        return row;
    };

    const makeEbitdaRows = (fieldName: SchemaFieldKeys): PanelRow[] => {
        const hasData = query.anyValue(fieldName);
        const display = FieldsSchema[fieldName]['display'] ?? fieldName;
        if (!hasData) {
            return null;
        }
        const ebitdaValuesRow = {
            ...defaults,
            field: {
                source: fieldName,
                display: display
            },
            value: findByYear()
        };

        const ebitdaMarginRow = {
            ...defaultsPercent,
            field: {
                source: fieldName,
                display: display + ' Margin'
            },
            value: findByYearDivideByRevenue()
        };
        return [ebitdaValuesRow, ebitdaMarginRow];
    };
    // const makeSectionRow = (req: SectionFieldRequest) => {
    //     return {
    //         ...defaults,
    //         field: {
    //             source: `${req.section}.${req.field}`,
    //             display: query.labelBySection(req)
    //         },
    //         value: findByYearSection({ ...req })
    //     };
    // };

    const ebitdaKeys: EBITDAFieldsKeys[] = DataAccessUtils.EBITDA_TYPES;

    const ebitdaRows = _.flatMap(ebitdaKeys, key => makeEbitdaRows(key) || []);

    const get_capitalized_software = (f, c) => {
        const year = parseInt(c.source);
        const getValue = (field: SchemaFieldKeys) => {
            const value = query.valueByYear(field, year);
            return value === 'N/A' ? 0 : value;
        };

        const override = query.valueByYear(
            'capitalized_software_override',
            year
        );

        if (override !== 'N/A') {
            return getFloat(override);
        }

        const capitalizedSoftware = getValue('capitalized_software');
        const capitalized_application_development = getValue(
            'capitalized_application_development'
        );
        const capitalized_rnd = getValue('capitalized_rnd');

        const total =
            capitalizedSoftware +
            capitalized_application_development +
            capitalized_rnd;
        if (total === 0) {
            return null;
        }

        return total;
    };

    const get_capex_plus_software = (f, c) => {
        const year = parseInt(c.source);
        const capitalized = get_capitalized_software(f, c) ?? 0;
        const capex = query.valueByYear('capex_per_year', year);

        if (capex === 'N/A') {
            return 'N/A';
        }

        return capex + capitalized;
    };
    const makeFieldRow = (
        data: { source: SchemaFieldKeys; display: string },
        type: 'dollar' | 'percent',
        valFn: 'by_year' | 'by_year_divide_by_revenue',
        extend: Partial<PanelRow> = {}
    ): PanelRow => {
        const valueCallback =
            valFn === 'by_year' ? findByYear(0) : findByYearDivideByRevenue(0);

        const defValues = type === 'dollar' ? defaults : defaultsPercent;
        return {
            ...defValues,
            field: {
                source: data.source,
                display: data.display
            },
            value: valueCallback,
            ...extend
        };
    };

    const makeDollarRow = (
        source: SchemaFieldKeys,
        display: string,
        extra: Partial<PanelRow> = {}
    ) => {
        return makeFieldRow({ source, display }, 'dollar', 'by_year', {
            editable: true,
            ...extra
        });
    };

    const makePercentRowByRevenue = (
        source: SchemaFieldKeys,
        display: string,
        extra: Partial<PanelRow> = {}
    ) => {
        return makeFieldRow(
            { source, display },
            'percent',
            'by_year_divide_by_revenue',
            {
                editable: false,
                ...extra
            }
        );
    };
    const rows: PanelRow[] = [
        makeDollarRow('net_revenue_per_year', 'Revenue'),
        makePercentRowByRevenue('net_revenue_per_year', 'Revenue Growth', {
            value: (c, f) => {
                const thisYear = findByYear()(c, f);
                const lastYear = findByYear(-1)(c, f);
                if (thisYear === 'N/A' || lastYear === 'N/A') {
                    return 'N/A';
                }

                const growth = (thisYear - lastYear) / lastYear;
                return growth;
            }
        }),

        makeDollarRow('gross_profit_per_year', 'Gross Profit'),
        makePercentRowByRevenue('gross_profit_per_year', 'Gross Margin'),
        makeDollarRow('ebitda_per_year', 'EBITDA'),
        makePercentRowByRevenue('ebitda_per_year', 'EBITDA Margin'),
        makeDollarRow('adjusted_ebitda_per_year', 'Adj. EBITDA'),
        makePercentRowByRevenue(
            'adjusted_ebitda_per_year',
            'Adj. EBITDA Margin'
        ),
        // ...ebitdaRows,
        makePercentRowByRevenue(
            'gross_retention_lost_only_per_year',
            'Gross Retention (Lost Only)',
            {
                editable: true,
                value: findByYear(),
                formatter: formatPercentNoMultiply
            }
        ),

        makePercentRowByRevenue('net_retention_yearly', 'Net Retention', {
            editable: true,
            value: findByYear(),
            formatter: formatPercentNoMultiply
        }),
        makeDollarRow('total_current_assets_yearly', 'Current Assets'),
        makeDollarRow(
            'total_current_liabilities_yearly',
            'Current Liabilities'
        ),
        makePercentRowByRevenue(null, 'NWC Ratio, % of Revenue', {
            value: (f, c) => {
                const year = parseInt(c.source);
                const assets = query.valueByYear(
                    'total_current_assets_yearly',
                    year
                );

                const Liabilities = query.valueByYear(
                    'total_current_liabilities_yearly',
                    year
                );

                const revenue = query.valueByYear('net_revenue_per_year', year);
                if (
                    assets === 'N/A' ||
                    Liabilities === 'N/A' ||
                    revenue === 'N/A'
                ) {
                    return 'N/A';
                }

                return (assets - Liabilities) / revenue;
            }
        }),

        makeDollarRow('capex_per_year', 'Capex'),
        makePercentRowByRevenue('capex_per_year', 'Capex, % of Revenue'),
        makeDollarRow(
            'capitalized_software_override',
            '⁠Capitalized Development Costs',
            {
                //Note we hack this 'field display name' to connect to sources in data access utils
                editable: true,
                value: get_capitalized_software
            }
        ),
        makeDollarRow(null, '⁠CapEx (incl Capitalized Development Costs)', {
            editable: false,
            value: get_capex_plus_software
        }),
        makePercentRowByRevenue(
            null,
            'CapEx (incl Capitalized Development Costs), as a % of revenue',
            {
                editable: false,
                value: (f, c) => {
                    //Special case where we edit a field but if not bring a grouped value
                    const year = parseInt(c.source);

                    const totalCapex = get_capex_plus_software(f, c);
                    const revenue = query.valueByYear(
                        'net_revenue_per_year',
                        year
                    );

                    if (totalCapex === 'N/A' || revenue === 'N/A') {
                        return 'N/A';
                    }

                    return totalCapex / revenue;
                }
            }
        ),
        makeDollarRow('ebitda_cashflow_yearly', 'Free Cash Flow (FCF)'),
        makePercentRowByRevenue(null, 'FCF Conversion, % of EBITDA', {
            value: (f, c) => {
                const year = parseInt(c.source);
                return query.fcf_conversion_ebitda(year);
            }
        })
    ];

    const ebitda = query.value('ebitda');
    if (ebitda) {
        var keys = Object.keys(ebitda);
        for (var k in keys) {
            const key = keys[k];
        }
    }

    return (
        <>
            <GetSize onResize={onResize} />
            <PanelTitle title="General" />
            <PanelTable
                className={style.panelYearly}
                topLeftCellText="($ in Millions)"
                columns={columns}
                rows={rows}
                disable_round_edges={true}
                onEditField={onEditReportField}
            />
        </>
    );
});
